import { z } from 'zod'

// ============================================================
// SCHEMA VALIDAZIONE ZOD PER E-QUIPE ERP
// ============================================================

// ------------------------------------------------------------
// AUTENTICAZIONE
// ------------------------------------------------------------

export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email obbligatoria')
    .email('Formato email non valido'),
  password: z
    .string()
    .min(6, 'La password deve essere di almeno 6 caratteri')
    .max(100, 'Password troppo lunga'),
})

export type LoginFormData = z.infer<typeof loginSchema>

// ------------------------------------------------------------
// CLIENTI
// ------------------------------------------------------------

export const createClientSchema = z.object({
  name: z
    .string()
    .min(2, 'Nome obbligatorio (min 2 caratteri)')
    .max(100, 'Nome troppo lungo'),
  status: z.enum(['PROSPECT', 'ACTIVE', 'INACTIVE', 'CHURNED']),
  tier: z.enum(['SILVER', 'GOLD', 'PLATINUM']),
  email: z.string().email('Formato email non valido').optional().or(z.literal('')),
  phone: z.string().optional(),
  website: z
    .string()
    .url('URL non valido')
    .optional()
    .or(z.literal('')),
  address: z.string().optional(),
  notes: z.string().max(2000, 'Note troppo lunghe').optional(),
})

export type CreateClientFormData = z.infer<typeof createClientSchema>

// Schema per aggiornamento (tutti i campi opzionali)
export const updateClientSchema = createClientSchema.partial()
export type UpdateClientFormData = z.infer<typeof updateClientSchema>

// ------------------------------------------------------------
// TASK
// ------------------------------------------------------------

export const createTaskSchema = z.object({
  title: z
    .string()
    .min(3, 'Titolo obbligatorio (min 3 caratteri)')
    .max(200, 'Titolo troppo lungo'),
  description: z.string().max(5000, 'Descrizione troppo lunga').optional(),
  status: z.enum(['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'BLOCKED', 'DONE', 'CANCELLED']).default('TODO'),
  priority: z.enum(['LOW', 'MEDIUM', 'HIGH', 'URGENT']).default('MEDIUM'),
  projectId: z.string().uuid('ID progetto non valido').optional(),
  clientId: z.string().uuid('ID cliente non valido').optional(),
  assigneeId: z.string().uuid('ID assegnatario non valido').optional(),
  dueDate: z
    .string()
    .refine((val) => !val || !isNaN(Date.parse(val)), 'Data non valida')
    .optional(),
  estimatedHours: z
    .number()
    .nonnegative('Deve essere positivo')
    .optional(),
})

export type CreateTaskFormData = z.infer<typeof createTaskSchema>

export const updateTaskSchema = createTaskSchema.partial()
export type UpdateTaskFormData = z.infer<typeof updateTaskSchema>

// ------------------------------------------------------------
// CONTENUTI EDITORIALI
// ------------------------------------------------------------

export const createContentSchema = z.object({
  title: z
    .string()
    .min(3, 'Titolo obbligatorio (min 3 caratteri)')
    .max(200, 'Titolo troppo lungo'),
  body: z.string().max(10000, 'Testo troppo lungo').optional(),
  type: z.enum(['POST', 'REEL', 'STORY', 'CAROUSEL', 'VIDEO', 'ARTICLE', 'INFOGRAPHIC']),
  platform: z.enum([
    'INSTAGRAM', 'FACEBOOK', 'LINKEDIN', 'TWITTER',
    'TIKTOK', 'YOUTUBE', 'PINTEREST',
  ]),
  status: z
    .enum(['IDEA', 'TODO', 'IN_PRODUCTION', 'READY', 'IN_REVIEW', 'CHANGES_REQUESTED', 'APPROVED', 'SCHEDULED', 'PUBLISHED'])
    .default('IDEA'),
  scheduledAt: z
    .string()
    .refine((val) => !val || !isNaN(Date.parse(val)), 'Data di pubblicazione non valida')
    .optional(),
  caption: z.string().max(2200, 'Caption troppo lunga').optional(),
  hashtags: z.array(z.string().max(100)).max(30, 'Massimo 30 hashtag').default([]),
  creatorId: z.string().uuid().optional(),
})

export type CreateContentFormData = z.infer<typeof createContentSchema>

// ------------------------------------------------------------
// PROGETTI
// ------------------------------------------------------------

export const createProjectSchema = z.object({
  name: z
    .string()
    .min(3, 'Nome obbligatorio (min 3 caratteri)')
    .max(150, 'Nome troppo lungo'),
  description: z.string().max(3000).optional(),
  status: z
    .enum(['DRAFT', 'PLANNING', 'ACTIVE', 'ON_HOLD', 'COMPLETED', 'CANCELLED'])
    .default('PLANNING'),
  clientId: z.string().uuid('ID cliente non valido').optional(),
  managerId: z.string().uuid('ID manager non valido').optional(),
  startDate: z
    .string()
    .refine((val) => !val || !isNaN(Date.parse(val)), 'Data inizio non valida')
    .optional(),
  endDate: z
    .string()
    .refine((val) => !val || !isNaN(Date.parse(val)), 'Data fine non valida')
    .optional(),
  budget: z.number().nonnegative('Budget deve essere positivo').optional(),
})

export type CreateProjectFormData = z.infer<typeof createProjectSchema>

// ------------------------------------------------------------
// PROFILO UTENTE
// ------------------------------------------------------------

export const updateProfileSchema = z.object({
  firstName: z.string().min(2, 'Nome obbligatorio').max(50),
  lastName: z.string().min(2, 'Cognome obbligatorio').max(50),
  phone: z.string().max(20).optional(),
  timezone: z.string().min(1, 'Fuso orario obbligatorio'),
})

export type UpdateProfileFormData = z.infer<typeof updateProfileSchema>

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Password attuale obbligatoria'),
    newPassword: z
      .string()
      .min(8, 'La nuova password deve essere di almeno 8 caratteri')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'La password deve contenere almeno una maiuscola, una minuscola e un numero'
      ),
    confirmPassword: z.string().min(1, 'Conferma password obbligatoria'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Le password non coincidono',
    path: ['confirmPassword'],
  })

export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>
